/**
 * Selectable-list request bodies and live-list / live-proposal rules.
 * Pure module: no DOM, no i18n. Messages are oldest-first.
 */

const SKIP_REFUSED = new Set(['invalid_value', 'other_target', 'pinned_scope']);

function isPresent(value) {
  return value !== undefined && value !== null && value !== '';
}

/**
 * @param {Object} [record]
 * @returns {{ entityType: string, entityId: string }|undefined}
 */
export function focusOf(record) {
  if (!record || !isPresent(record.entityType) || !isPresent(record.entityId)) return undefined;
  return { entityType: record.entityType, entityId: record.entityId };
}

/**
 * POST /api/llm/messages body. Omits unset list fields. Does not set x-ai-blocks.
 * @param {Object} turn
 * @returns {Object}
 */
export function buildMessageRequest(turn) {
  const body = { message: turn?.message };
  if (isPresent(turn?.conversationId)) body.conversationId = turn.conversationId;
  if (isPresent(turn?.listId)) body.listId = turn.listId;
  const focus = focusOf(turn?.focus);
  if (focus) body.focus = focus;
  if (isPresent(turn?.action)) body.action = turn.action;
  if (isPresent(turn?.proposalId)) body.proposalId = turn.proposalId;
  return body;
}

/** @param {string} message */
export function turnForPlainMessage(message) {
  return { message };
}

/**
 * Typed send. Comment mode posts immediately. A pin without comment mode
 * attaches focus and no action.
 * @param {{ text: string, pin: Object|null, commenting: boolean }} input
 */
export function turnForComposerSend({ text, pin, commenting }) {
  if (pin && commenting) {
    return {
      message: text,
      listId: pin.listId,
      focus: focusOf(pin),
      action: 'comment',
    };
  }
  if (pin) {
    const turn = { message: text, listId: pin.listId };
    const focus = focusOf(pin);
    if (focus) turn.focus = focus;
    return turn;
  }
  return { message: text };
}

/**
 * Show more and summarize-this-page. Never includes focus.
 * @param {'next_page'|'summarize_list'} action
 * @param {Object} listBlock
 * @param {string} message
 */
export function turnForPageAction(action, listBlock, message) {
  return {
    message,
    listId: listBlock.listId,
    action,
  };
}

/** @param {Object} pin @param {string} message */
export function turnForSummarizeRecord(pin, message) {
  return {
    message,
    listId: pin.listId,
    focus: focusOf(pin),
    action: 'summarize_record',
  };
}

/** @param {Object} proposal @param {string} message */
export function turnForConfirm(proposal, message) {
  const turn = {
    message,
    listId: proposal.listId,
    action: 'confirm_update',
    proposalId: proposal.proposalId,
  };
  const focus = focusOf(proposal);
  if (focus) turn.focus = focus;
  return turn;
}

/** Cancel does not send focus. @param {Object} proposal @param {string} message */
export function turnForCancel(proposal, message) {
  return {
    message,
    listId: proposal.listId,
    action: 'cancel_update',
    proposalId: proposal.proposalId,
  };
}

/**
 * @param {Object} listBlock
 * @param {Object} row
 */
export function pinFromRow(listBlock, row) {
  return {
    listId: listBlock.listId,
    entityType: listBlock.entityType,
    entityId: row.entityId,
    index: row.index,
    label: row.label ?? '',
    canComment: listBlock.canComment !== false,
  };
}

/** @param {Object|null} pin @param {Object} listBlock @param {Object} row */
export function isRowPinned(pin, listBlock, row) {
  if (!pin || !listBlock || !row) return false;
  return pin.listId === listBlock.listId && isPresent(pin.entityId) && pin.entityId === row.entityId;
}

/**
 * Server index, including when the row has no label.
 * @param {Object} row
 */
export function rowIndex(row) {
  return row?.index;
}

/**
 * Subtitle and up to three field values, empties skipped, joined with " · ".
 * @param {Object} row
 * @returns {string}
 */
export function rowSecondaryLine(row) {
  const parts = [];
  if (isPresent(row?.subtitle)) parts.push(String(row.subtitle));
  const fields = Array.isArray(row?.fields) ? row.fields.slice(0, 3) : [];
  fields.forEach((field) => {
    if (!isPresent(field?.value)) return;
    parts.push(String(field.value));
  });
  return parts.join(' · ');
}

/**
 * @param {Object} block
 * @returns {{ from: number, to: number, total: number|null }}
 */
export function listWindowBounds(block) {
  const skip = typeof block?.skip === 'number' ? block.skip : 0;
  const count = Array.isArray(block?.rows) ? block.rows.length : 0;
  return {
    from: skip + 1,
    to: skip + count,
    total: typeof block?.total === 'number' ? block.total : null,
  };
}

/** @param {Object} block @returns {number} */
export function listPageSize(block) {
  if (typeof block?.take === 'number' && block.take > 0) return block.take;
  return Array.isArray(block?.rows) ? block.rows.length : 0;
}

/**
 * Show more when the id is not exhausted, and total is missing or the window
 * has not reached it.
 * @param {Object} block
 * @param {Set<string>} [exhaustedListIds]
 */
export function canShowMore(block, exhaustedListIds) {
  if (!isPresent(block?.listId)) return false;
  if (exhaustedListIds && exhaustedListIds.has(block.listId)) return false;
  if (typeof block.total !== 'number') return true;
  const skip = typeof block.skip === 'number' ? block.skip : 0;
  const count = Array.isArray(block.rows) ? block.rows.length : 0;
  return skip + count < block.total;
}

/**
 * Last entity_list in the thread. The in-flight message counts when it is in the array.
 * @param {Array} messages
 * @returns {{ messageId: string, partIndex: number, block: Object }|null}
 */
export function findNewestEntityList(messages) {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const parts = messages[i]?.parts;
    if (!Array.isArray(parts)) continue;
    for (let p = parts.length - 1; p >= 0; p--) {
      const part = parts[p];
      if (part?.kind === 'block' && part.block?.type === 'entity_list') {
        return { messageId: messages[i]._id, partIndex: p, block: part.block };
      }
    }
  }
  return null;
}

/** @param {{ messageId: string, partIndex: number }|null} newest */
export function isNewestEntityList(newest, messageId, partIndex) {
  return !!newest && newest.messageId === messageId && newest.partIndex === partIndex;
}

function lastProposal(message) {
  const parts = message?.parts;
  if (!Array.isArray(parts)) return null;
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part?.kind === 'block' && part.block?.type === 'update_proposal') return part.block;
  }
  return null;
}

/**
 * Which review card still has Confirm and Cancel.
 * While streaming, only a proposal in the live buffer counts.
 * @param {Array} messages
 * @param {{ streaming?: boolean }} [options]
 * @returns {{ messageId: string, block: Object }|null}
 */
export function findLiveProposal(messages, { streaming = false } = {}) {
  if (!Array.isArray(messages) || messages.length === 0) return null;

  if (streaming) {
    const buffer = [...messages].reverse().find((message) => message?.isStreaming);
    const block = lastProposal(buffer);
    if (!block) return null;
    return { messageId: buffer._id, block };
  }

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    // A local error bubble is not a server turn, so it does not retire the previous card.
    if (!message || message.role !== 'assistant' || message.isStreaming || message.isError) continue;
    const block = lastProposal(message);
    if (block) return { messageId: message._id, block };
    const refused = message.metadata?.refused;
    if (SKIP_REFUSED.has(refused)) continue;
    return null;
  }
  return null;
}

/**
 * Clear the pin when a newer list id arrives. Leave it when the thread has no list.
 * @param {Object|null} pin
 * @param {Array} messages
 */
export function pinAfterNewestList(pin, messages) {
  if (!pin) return null;
  const newest = findNewestEntityList(messages);
  if (newest && newest.block.listId !== pin.listId) return null;
  return pin;
}

function refusedListId(metadata, turn) {
  if (isPresent(metadata?.listId)) return metadata.listId;
  if (isPresent(metadata?.list_id)) return metadata.list_id;
  if (isPresent(turn?.listId)) return turn.listId;
  return null;
}

/**
 * After a turn is saved: exhaust a stale list, exhaust a next_page that did
 * not advance, and drop the pin when the newest list id changed.
 * A same-id next_page does not clear the pin.
 * @param {Object} input
 * @param {Object|null} input.pin
 * @param {Set<string>} input.exhaustedListIds
 * @param {Object} input.turn
 * @param {Array} input.messages
 * @param {Object} [input.metadata]
 */
export function reduceListSelection({ pin, exhaustedListIds, turn, messages, metadata }) {
  const exhausted = new Set(exhaustedListIds || []);
  let nextPin = pin || null;
  const newestId = findNewestEntityList(messages)?.block?.listId ?? null;

  if (turn?.action === 'next_page' && isPresent(turn.listId) && newestId === turn.listId) {
    exhausted.add(turn.listId);
  }

  if (nextPin && newestId && newestId !== nextPin.listId) {
    nextPin = null;
  }

  if (metadata?.refused === 'list_stale') {
    const id = refusedListId(metadata, turn);
    if (id) {
      exhausted.add(id);
      if (nextPin?.listId === id) nextPin = null;
    }
  }

  return { pin: nextPin, exhaustedListIds: exhausted };
}

/**
 * Fingerprint of list and proposal blocks, so a text delta can patch in place
 * and a new block can re-render the thread.
 * @param {Array} [parts]
 */
export function selectionBlockKey(parts) {
  if (!Array.isArray(parts)) return '';
  const bits = [];
  parts.forEach((part) => {
    if (part?.kind !== 'block') return;
    const type = part.block?.type;
    if (type !== 'entity_list' && type !== 'update_proposal') return;
    bits.push(`${type}:${part.block.listId || ''}:${part.block.proposalId || ''}`);
  });
  return bits.join('|');
}
